import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";

/**
 * Viewer draft mode, ref-counted.
 *
 * The viewer drops to a cheap rebuild between `beginDraft()` and `endDraft()`
 * (64 lathe segments, events suppressed, one high-res rebuild at the end).
 * Every slider drag must be wrapped, so the kit does it for you: `Slider` and
 * `NumberField` call `begin()` on drag start and `end()` on drag end via this
 * context. Outside a `DraftProvider` the controller is a no-op, which is why
 * the legacy band/engagement trees keep working unchanged.
 *
 * Ref-counting is the whole point: two overlapping drags (or a tab that calls
 * `beginDraft()` itself mid-drag) must not strand the viewer in draft mode.
 * N begins need N ends; an unmatched `end()` is ignored.
 */
export interface DraftController {
  /** Enter draft mode (only the outermost call reaches the viewer). */
  begin(): void;
  /** Leave draft mode when the last matching `begin()` is released. */
  end(): void;
}

const NOOP: DraftController = { begin() {}, end() {} };

const DraftContext = createContext<DraftController>(NOOP);

export function DraftProvider({ controller, children }: { controller: DraftController; children: ReactNode }) {
  return <DraftContext.Provider value={controller}>{children}</DraftContext.Provider>;
}

/** The ambient draft controller; a no-op when there is no provider above. */
export function useDraft(): DraftController {
  return useContext(DraftContext);
}

/**
 * Wraps raw `beginDraft` / `endDraft` RPCs in a ref-counted controller with a
 * stable identity. `begin` / `end` may be fresh closures every render (the RPC
 * proxy hands out a new function per property access) — they are read through a
 * ref, so the returned controller never changes.
 */
export function useDraftController(begin: () => void, end: () => void): DraftController {
  const latest = useRef({ begin, end });
  latest.current = { begin, end };
  const depth = useRef(0);
  return useMemo<DraftController>(() => ({
    begin() {
      if (depth.current++ === 0) latest.current.begin();
    },
    end() {
      if (depth.current === 0) return;
      if (--depth.current === 0) latest.current.end();
    },
  }), []);
}

export interface DragDraftOptions {
  /** Wrap the drag in viewer draft mode. Default true. */
  draft?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

/**
 * Drag lifecycle for a range input: pointer-down starts, and the drag ends on
 * pointer-up anywhere (release outside the track), pointer-cancel, or unmount.
 * Guaranteed balanced — a drag that ends without ever firing `onChange` still
 * closes its draft, and flipping the `draft` prop mid-drag cannot orphan one.
 */
export function useDragDraft({ draft = true, onDragStart, onDragEnd }: DragDraftOptions) {
  const ctl = useDraft();
  const latest = useRef({ ctl, draft, onDragStart, onDragEnd });
  latest.current = { ctl, draft, onDragStart, onDragEnd };
  /** null = idle; boolean = dragging, and whether this drag opened a draft. */
  const drafted = useRef<boolean | null>(null);

  const stop = useCallback(() => {
    const opened = drafted.current;
    if (opened === null) return;
    drafted.current = null;
    latest.current.onDragEnd?.();
    if (opened) latest.current.ctl.end();
  }, []);

  const start = useCallback(() => {
    if (drafted.current !== null) return;
    const { draft: on, ctl: c, onDragStart: cb } = latest.current;
    drafted.current = on;
    if (on) c.begin();
    cb?.();
    window.addEventListener("pointerup", stop, { once: true });
    window.addEventListener("pointercancel", stop, { once: true });
  }, [stop]);

  // Never leave the viewer in draft mode because a tab unmounted mid-drag.
  useEffect(() => () => stop(), [stop]);

  return { onPointerDown: start };
}
